import React, { FC, memo, useEffect, useRef, useState } from 'react'
import { format } from 'date-fns'

import { IInventoryEvent, IProduct } from '../../types/products'
import { IAccount, IBranch } from '../../types/user'
import { IPaginationState } from '../../types/common'
import useThunkDispatch from '../../common/useThunkDispatch'
import {
  requestInventoryEvents,
  IInventoryEventsResult,
} from './inventoryActions'
import Tooltip from '../../components/Tooltip'

interface IProductFormProps {
  product: IProduct
  branch: IBranch
  account?: IAccount
  onProductRefresh: (
    patch: Partial<Pick<IProduct, 'inventory' | 'updatedAt'>>,
  ) => void
}

const PER_PAGE = 25

const EMPTY_PAGINATION_STATE: IPaginationState = {
  endCursor: '',
  startCursor: '',
  hasNextPage: false,
  hasPreviousPage: false,
}

const stateMapping = {
  manual: 'Manual adjustment',
  transfer_to: 'Added from transfer',
  transfer_from: 'Transferred',
  shipment: 'Added from shipment',
  checkout: 'Checkout',
  cycle_count: 'Count',
  move_out: 'Moved out',
  move_in: 'Moved in',
}

const InventoryEvents: FC<IProductFormProps> = ({
  product,
  branch,
  account,
  onProductRefresh,
}) => {
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [inventoryEvents, setInventoryEvents] = useState<IInventoryEvent[]>([])
  const [paginationState, setPaginationState] = useState<IPaginationState>(
    EMPTY_PAGINATION_STATE,
  )
  const sentinelRef = useRef<HTMLDivElement>(null)

  const dispatch = useThunkDispatch()
  const branchId = branch && branch.id

  const loadInventoryEvents = async () => {
    setLoading(true)
    const result = await dispatch<Promise<IInventoryEventsResult>>(
      requestInventoryEvents(product.id, branch.id, account && account.id, {
        first: PER_PAGE,
      }),
    )
    setInventoryEvents(result?.events || [])
    setPaginationState(result?.paginationState || EMPTY_PAGINATION_STATE)
    onProductRefresh({
      inventory: result?.inventory,
      updatedAt: result?.updatedAt,
    })
    setLoading(false)
  }

  const loadMoreEvents = async () => {
    setLoadingMore(true)
    const result = await dispatch<Promise<IInventoryEventsResult>>(
      requestInventoryEvents(product.id, branch.id, account && account.id, {
        first: PER_PAGE,
        after: paginationState.endCursor,
      }),
    )
    setInventoryEvents((events) => [...events, ...(result?.events || [])])
    setPaginationState(result?.paginationState || EMPTY_PAGINATION_STATE)
    setLoadingMore(false)
  }

  useEffect(() => {
    if (branchId) {
      loadInventoryEvents()
    }
  }, [product.id, branchId])

  // fetch the next page once the sentinel below the list scrolls into view
  useEffect(() => {
    if (loading || loadingMore || !paginationState.hasNextPage) return

    const sentinel = sentinelRef.current
    if (!sentinel) return

    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) loadMoreEvents()
    })
    observer.observe(sentinel)

    return () => observer.disconnect()
  }, [loading, loadingMore, paginationState])

  return (
    <div className="product-inventory">
      {loading && <div className="list_placeholder">Loading history...</div>}
      {!loading && !inventoryEvents.length && (
        <div className="list_placeholder">No history yet</div>
      )}
      {!loading &&
        inventoryEvents.map((inventoryEvent) => (
          <div key={inventoryEvent.id} className="product-inventory_item">
            <div
              className={`product-inventory_amount ${
                inventoryEvent.amount < 0 && '-negative'
              }`}
            >
              {inventoryEvent.amount >= 0
                ? `+${inventoryEvent.amount}`
                : inventoryEvent.amount}
            </div>
            <div className="product-inventory_stock_value">
              {inventoryEvent.stockValue}{' '}
              {[
                'cycle_count',
                'move_out',
                'move_in',
                'checkout',
                'transfer_from',
                'transfer_to',
                'manual',
              ].includes(inventoryEvent.eventType) &&
              inventoryEvent.additional &&
              inventoryEvent.additional.locationCode
                ? `in ${inventoryEvent.additional.locationCode}`
                : 'in stock'}{' '}
              ({inventoryEvent.unit})
            </div>
            <div className="product-inventory_date">
              {['transfer_to', 'transfer_from'].includes(
                inventoryEvent.eventType,
              ) && (
                <Tooltip id={`tooltip-branch-${inventoryEvent.id}`}>
                  <div
                    className="input_label-tooltip"
                    style={{
                      bottom: 0,
                      textDecorationStyle: 'dotted',
                      textDecorationLine: 'underline',
                      marginLeft: -4,
                      cursor: 'pointer',
                    }}
                    data-tip={`${
                      inventoryEvent.eventType === 'transfer_to' ? 'from' : 'to'
                    } ${inventoryEvent.additional.branchName}, #${
                      inventoryEvent.additional.id
                    }`}
                    data-for={`tooltip-branch-${inventoryEvent.id}`}
                    data-effect="solid"
                    data-place="bottom"
                  >
                    {stateMapping[inventoryEvent.eventType]}
                  </div>
                  &nbsp;
                </Tooltip>
              )}
              {[
                'manual',
                'shipment',
                'checkout',
                'cycle_count',
                'move_out',
                'move_in',
              ].includes(inventoryEvent.eventType) && (
                <>
                  {stateMapping[inventoryEvent.eventType]}
                  {inventoryEvent.eventType === 'manual' &&
                    inventoryEvent.additional?.locationCode && (
                      <> in {inventoryEvent.additional.locationCode}</>
                    )}
                  {inventoryEvent.eventType === 'checkout' &&
                    inventoryEvent.additional?.locationCode && (
                      <> in {inventoryEvent.additional.locationCode}</>
                    )}
                </>
              )}
              {inventoryEvent.createdByUser &&
                inventoryEvent.createdByUser.id !== -1 && (
                  <>
                    &nbsp;by&nbsp;
                    {inventoryEvent.createdByUser.name}
                  </>
                )}
              ,&nbsp;
              <span style={{ whiteSpace: 'nowrap' }}>
                {format(inventoryEvent.eventCreatedAt, 'MMM DD, YYYY h:mm A')}
              </span>
              {inventoryEvent.eventType === 'manual' &&
                inventoryEvent.additional?.reason && (
                  <>
                    <br />"{inventoryEvent.additional.reason}"
                  </>
                )}
            </div>
          </div>
        ))}
      {!loading && paginationState.hasNextPage && (
        <div ref={sentinelRef} className="list_placeholder">
          {loadingMore && 'Loading more...'}
        </div>
      )}
    </div>
  )
}

export default memo(InventoryEvents)
